import { useCallback, useMemo } from "react";
import { shallowEqual, useSelector } from "react-redux";

import { cleverTapCommonEvents, cleverTapCommonWords } from "../../analytics/clevertapConstants";
import useAnalytics from "../../analytics/useAnalytics";
import { COUNTRIES } from "../../constants/common/common";
import { LAYOUT_TYPE, WIDGET_TYPE } from "../../constants/pageConstants/dynamicHomeConstants";
import {
  LAST_VISITED_ADDRESS,
  LAST_VISITED_INTERNATIONAL_LOCATION,
  LAST_VISITED_PINCODE,
} from "../../constants/storage";
import CookieUtil from "../storage/cookieUtil";

/**
 * Converts truthy widget flags into booleans (CDP format).
 * @param {boolean|string|number} value flag value
 * @returns {boolean}
 */
const toBool = (value) => value === true || value === "Y" || value === 1;

/**
 * Maps internal layout type values to analytics `widget_layout_type` labels.
 * @param {string} [layoutType] - Layout snapshot type from CMS
 * @returns {string|undefined}
 */
export const mapWidgetLayoutTypeLabel = (layoutType) => {
  if (!layoutType) return undefined;
  const lower = String(layoutType).toLowerCase();
  if (lower === LAYOUT_TYPE.GRID || lower === "grid") return "Grid";
  if (lower === LAYOUT_TYPE.CAROUSEL || lower === "carousel") return "Carousel";
  if (lower === "bento") return "Bento";
  if (lower === "product_tile" || lower === "producttile") return "Product Tile";
  return layoutType;
};

/**
 * Normalizes international flags for analytics payloads.
 * @param {boolean|string|number|undefined|null} value - Raw flag from item or widget
 * @returns {boolean}
 */
const toInternationalBool = (value) => value === true || value === "Y" || value === 1 || value === "yes";

/**
 * Widget-level fields for Widget Clicked (snake_case and camelCase accepted).
 * @param {object} widget - Normalized widget analytics object from `buildDynamicHomeWidgetPayload`
 * @returns {object} Flags for payload
 */
function pickWidgetFlags(widget) {
  const w = widget || {};
  return {
    is_carousel: toBool(w.is_carousel ?? w.isCarousel),
    is_having_product: toBool(w.is_having_product ?? w.isHavingProduct),
    is_having_image: toBool(w.is_having_image ?? w.isHavingImage),
    is_having_cta: toBool(w.is_having_cta ?? w.isHavingCta),
    is_having_video: toBool(w.is_having_video ?? w.isHavingVideo),
    is_masthead: toBool(w.is_masthead ?? w.isMasthead),
  };
}

/**
 * Builds widget-level metadata for dynamic-home Widget Clicked events.
 * @param {object} widget - Raw widget config from page JSON
 * @param {number} order - Widget index on page
 * @param {object} [options] - Extra context flags
 * @param {boolean} [options.isMasthead=false] - True if widget rendered inside mobile masthead
 * @param {string} [options.storeName] - Selected masthead brand/store name
 * @param {object} [options.tracking] - CleverTap/CDP targeting from feed (e.g. campaignId, group, segmentId); pass from `Widget` `tracking` prop
 * @returns {object} Payload fragment merged into `triggerWidgetItemClickEvent` `widget` argument
 */
export function buildDynamicHomeWidgetPayload(widget, order, { isMasthead = false, storeName, tracking } = {}) {
  if (!widget) return {};
  const { widgetType, sectionSettings, layoutSnapshot } = widget;
  const headerCta = sectionSettings?.header?.cta;
  const footerCta = sectionSettings?.footer?.cta;
  const layoutType = layoutSnapshot?.type;

  const isProductWidget =
    widgetType === WIDGET_TYPE.PRODUCT_COLLECTION || widgetType === WIDGET_TYPE.PRODUCTS_WITH_TABS;
  const isCollections = widgetType === WIDGET_TYPE.COLLECTIONS;
  const isVideoWidget = widgetType === WIDGET_TYPE.VIDEO;

  const trackingPayload =
    tracking && typeof tracking === "object" && Object.keys(tracking).length > 0 ? { ...tracking } : undefined;

  return {
    name: widget.name ?? widget.key,
    id: widget.key ?? widget.widgetId,
    position: order,
    layoutType: mapWidgetLayoutTypeLabel(layoutType),
    isCarousel:
      layoutType === LAYOUT_TYPE.CAROUSEL ||
      (isVideoWidget && Array.isArray(widget.typeConfig?.stories) && widget.typeConfig.stories.length > 1),
    isMasthead: isMasthead || widgetType === "MASTHEAD",
    storeName: storeName ?? widget.storeName ?? widget.store_name,
    is_having_cta: Boolean(headerCta || footerCta),
    is_having_product: isProductWidget,
    is_having_image: isCollections || isProductWidget,
    is_having_video: isVideoWidget,
    tracking: trackingPayload,
  };
}

/**
 * Default dynamic-home widget analytics context (cookies + Redux + HOME page type).
 * Matches `locationLockHeader` domestic merge and home duplicate payload shape.
 * @returns {Object} context fields for widget click payloads
 */
function useDefaultDynamicHomeWidgetContext() {
  const catalogId = useSelector((state) => state.appConfig.catalogId);
  const isMobile = useSelector((state) => state.appConfig.isMobile);
  const mobileStoreCount = useSelector((state) => state.dynamicHome?.pageStores?.length ?? 0);
  const { selectedDomesticLocation, selectedIntLocation } = useSelector(
    (state) => ({
      selectedDomesticLocation: state.locationLock?.selectedDomesticLocation,
      selectedIntLocation: state.locationLock?.selectedIntLocation,
    }),
    shallowEqual,
  );

  const lastVisitedPincode = CookieUtil.getCookie(LAST_VISITED_PINCODE);
  const lastVisitedAddress = CookieUtil.getCookie(LAST_VISITED_ADDRESS);
  const lastVisitedIntlLocation = CookieUtil.getCookie(LAST_VISITED_INTERNATIONAL_LOCATION);

  return useMemo(() => {
    const isIndia = catalogId === COUNTRIES.INDIA;
    const domesticPincode = lastVisitedPincode || selectedDomesticLocation?.pincode;
    const domesticArea = lastVisitedAddress || selectedDomesticLocation?.address;

    return {
      page_name: cleverTapCommonWords.HOME,
      page_type: cleverTapCommonWords.HOME,
      pincode: isIndia ? (domesticPincode ?? null) : null,
      area: isIndia ? (domesticArea ?? null) : null,
      system_pincode: isIndia ? (domesticPincode ?? null) : null,
      country: catalogId,
      city: isIndia ? null : selectedIntLocation?.location || lastVisitedIntlLocation || null,
      platform: isMobile ? "mobile" : "desktop",
      mobileStoreCount,
    };
  }, [
    catalogId,
    isMobile,
    mobileStoreCount,
    lastVisitedAddress,
    lastVisitedIntlLocation,
    lastVisitedPincode,
    selectedDomesticLocation?.address,
    selectedDomesticLocation?.pincode,
    selectedIntLocation?.location,
  ]);
}

/**
 * Custom hook that returns a pre-bound widget item click tracker.
 * Internally consumes useAnalytics so callers don't need to pass trackEvent.
 * Resolves default HOME context (page_name, pincode, city, etc.) inside the hook.
 * @returns {{ triggerWidgetItemClickEvent: Function, triggerWidgetClickEvent: Function }}
 */
const useWidgetClickEvent = () => {
  const { trackEvent } = useAnalytics();
  const defaultHomeWidgetContext = useDefaultDynamicHomeWidgetContext();

  /**
   * Fires the "Widget Clicked" CleverTap / GA4 / CDP event.
   * @param {Object} params
   * @param {string} [params.action="Image Clicked"] - Image Clicked | CTA Clicked | Product Clicked | Video Clicked | Store Clicked | Tab Clicked
   * @param {Object} [params.widget={}] - Widget-level metadata
   * @param {Object} [params.item={}] - Item / cell-level metadata
   * @param {Object} [params.context={}] - Optional overrides for page / location context
   * @returns {void}
   */
  const triggerWidgetItemClickEvent = useCallback(
    ({ action = "Image Clicked", widget = {}, item = {}, context = {} } = {}) => {
      const mergedContext = { ...defaultHomeWidgetContext, ...context };
      const wf = pickWidgetFlags(widget);
      const itemIntl =
        item.is_international !== undefined && item.is_international !== null
          ? item.is_international
          : (widget.is_international ?? widget.isInternational);
      const widgetIntl = widget.is_international ?? widget.isInternational;
      const isInternationalResolved =
        itemIntl !== undefined && itemIntl !== null
          ? toInternationalBool(itemIntl)
          : widgetIntl !== undefined && widgetIntl !== null
            ? toInternationalBool(widgetIntl)
            : false;

      const data = {
        action,
        widget_name: widget.name,
        widget_id: widget.id,
        widget_position: widget.position,
        widget_layout_type: widget.layoutType,

        page_name: mergedContext.page_name ?? mergedContext.pageName,
        page_type: mergedContext.page_type ?? mergedContext.pageType ?? "HOME",
        pincode: mergedContext.pincode,
        area: mergedContext.area,
        system_pincode: mergedContext.system_pincode ?? mergedContext.systemPincode,
        country: mergedContext.country,
        city: mergedContext.city,
        browsing_city: mergedContext.browsing_city ?? mergedContext.browsingCity,
        platform: mergedContext.platform,

        pid: item.pid ?? item.productId,
        product_category: item.product_category ?? item.productCategory ?? item.primaryCategoryId,
        sku_id: item.sku_id,
        redirection_url: item.redirection_url ?? item.redirectionUrl ?? item.linkUrl,
        item_name: item.item_name ?? item.itemName ?? item.title,
        item_row: item.item_row ?? item.row,
        item_column: item.item_column ?? item.column,
        item_position: item.item_position ?? item.position,
        store_name: item.store_name ?? item.storeName ?? widget.store_name ?? widget.storeName,

        ...wf,
        /** Mobile with multiple brand stores → treat as masthead */
        is_masthead:
          (mergedContext.platform === "mobile" && (mergedContext.mobileStoreCount ?? 0) > 1) || wf.is_masthead,
        /** Boolean for CDP batch schema (matches `/event/v1/track/batch`) */
        is_international: isInternationalResolved,

        campaignId: widget.tracking?.campaignId,
        group: widget.tracking?.group,
        segmentId: widget.tracking?.segmentId,
      };

      trackEvent(cleverTapCommonEvents.widgetClicked, data, ["clevertap"]);
      trackEvent(cleverTapCommonEvents.widgetClicked, data, ["CDP"]);
    },
    [defaultHomeWidgetContext, trackEvent],
  );

  return { triggerWidgetItemClickEvent, triggerWidgetClickEvent: triggerWidgetItemClickEvent };
};

export default useWidgetClickEvent;
